// src/components/Funnels/SkillToSaleQuizFunnel/quizLogic.ts
// All logic around multipliers, product ladder, roadmap phases and calculateSimulation
export type ExperienceLevel =
  | "Beginner (0-1 years)"
  | "Intermediate (2-3 years)"
  | "Advanced (4-7 years)"
  | "Expert (8+ years)"
  | "Industry Leader/Recognized Authority";

export type AudienceSize =
  | "No audience (0-100 followers)"
  | "Small audience (100-1,000)"
  | "Growing audience (1K-10K)"
  | "Established audience (10K-50K)"
  | "Large audience (50K+)";

export type TimeCommitment =
  | "1-5 hours (Side hustle)"
  | "6-15 hours (Part-time)"
  | "16-30 hours (Serious commitment)"
  | "31-40 hours (Full-time)"
  | "40+ hours (All-in entrepreneur)";

export type KnowledgeArea =
  | "Digital Marketing & Social Media"
  | "Business & Entrepreneurship"
  | "Finance & Investment"
  | "Technology & Programming"
  | "Health & Fitness"
  | "Personal Development"
  | "Relationships & Dating"
  | "Photography & Video"
  | "Writing & Content Creation"
  | "Cooking & Food"
  | "Fashion & Beauty"
  | "Real Estate"
  | "Other Professional Skill";

export interface QuizData {
  knowledge: string;
  experience: string;
  audience: string;
  timeCommitment: string;
  currentIncome: string;
  goal: string;
  email: string;
  name: string;
}

export interface Product {
  name: string;
  price: string;
  description: string;
  targetSales: string;
  timeline: string;
}

export interface ProductLadder {
  phase1: Product;
  phase2: Product;
  phase3: Product;
}

export interface RoadmapPhase {
  phase: number;
  title: string;
  timeline: string;
  revenue: number;
  keyActions: string[];
  milestone: string;
}

export interface Strategy {
  difficulty: string;
  timeline: string;
  focus: string;
  color: string;
  confidence: string;
}

export interface Simulation {
  currentPotential: number;
  monthlyGoal: number;
  timeToGoal: number;
  strategy: Strategy;
  productLadder: ProductLadder;
  roadmapPhases: RoadmapPhase[];
  successProbability: number;
}

const MULTIPLIERS = {
  experience: {
    "Beginner (0-1 years)": 0.5,
    "Intermediate (2-3 years)": 1,
    "Advanced (4-7 years)": 1.5,
    "Expert (8+ years)": 2,
    "Industry Leader/Recognized Authority": 3,
  } as Record<ExperienceLevel, number>,

  audience: {
    "No audience (0-100 followers)": 0.3,
    "Small audience (100-1,000)": 0.6,
    "Growing audience (1K-10K)": 1,
    "Established audience (10K-50K)": 1.8,
    "Large audience (50K+)": 2.5,
  } as Record<AudienceSize, number>,

  time: {
    "1-5 hours (Side hustle)": 0.4,
    "6-15 hours (Part-time)": 0.7,
    "16-30 hours (Serious commitment)": 1,
    "31-40 hours (Full-time)": 1.3,
    "40+ hours (All-in entrepreneur)": 1.6,
  } as Record<TimeCommitment, number>,

  baseRevenue: {
    "Digital Marketing & Social Media": 800000,
    "Business & Entrepreneurship": 1200000,
    "Finance & Investment": 1500000,
    "Technology & Programming": 1000000,
    "Health & Fitness": 600000,
    "Personal Development": 900000,
    "Relationships & Dating": 700000,
    "Photography & Video": 500000,
    "Writing & Content Creation": 400000,
    "Cooking & Food": 300000,
    "Fashion & Beauty": 450000,
    "Real Estate": 1100000,
    "Other Professional Skill": 600000,
  } as Record<KnowledgeArea, number>,
};

const parseGoalRevenue = (goalString: string): number => {
  const matched = goalString.match(/₦?([\d,KM+]+)/i)?.[1];
  if (!matched) return 1000000;
  let s = matched.toUpperCase();
  s = s.replace(/,/g, "");
  if (s.endsWith("+")) s = s.slice(0, -1);
  // support K and M
  if (s.endsWith("K")) {
    return parseFloat(s.slice(0, -1)) * 1000;
  }
  if (s.endsWith("M")) {
    return parseFloat(s.slice(0, -1)) * 1000000;
  }
  return parseInt(s, 10) || 1000000;
};

const getProductLadder = (
  knowledge: KnowledgeArea,
  goalRevenue: number
): ProductLadder => {
  const productsByNiche: Record<KnowledgeArea, any> = {
    "Digital Marketing & Social Media": {
      starter: {
        name: "Social Media Templates Pack",
        price: "₦5K-10K",
        description: "Ready-to-use templates for all platforms",
      },
      growth: {
        name: "Instagram Marketing Masterclass",
        price: "₦25K-50K",
        description:
          "Complete video course on growing and monetizing Instagram",
      },
      premium: {
        name: "Done-For-You Marketing System",
        price: "₦200K-500K",
        description: "Full marketing automation setup + 3-month support",
      },
    },
    "Business & Entrepreneurship": {
      starter: {
        name: "Business Plan Template & Guide",
        price: "₦8K-15K",
        description: "Professional templates with step-by-step instructions",
      },
      growth: {
        name: "6-Week Business Launch Program",
        price: "₦50K-100K",
        description: "From idea to first sales with weekly coaching calls",
      },
      premium: {
        name: "Million Naira Business Blueprint",
        price: "₦300K-800K",
        description: "Complete business setup + 6-month mentorship",
      },
    },
    "Finance & Investment": {
      starter: {
        name: "Investment Basics Ebook",
        price: "₦5K-12K",
        description: "Beginner-friendly guide to Nigerian investment options",
      },
      growth: {
        name: "Crypto & Stock Trading Course",
        price: "₦40K-80K",
        description: "Advanced strategies with live trading sessions",
      },
      premium: {
        name: "Personal Wealth Management",
        price: "₦250K-600K",
        description: "1-on-1 financial planning + portfolio management",
      },
    },
    "Technology & Programming": {
      starter: {
        name: "Coding Cheat Sheets Bundle",
        price: "₦8K-15K",
        description: "Quick reference guides for popular languages",
      },
      growth: {
        name: "Full Stack Development Bootcamp",
        price: "₦60K-120K",
        description: "12-week intensive program with projects",
      },
      premium: {
        name: "Custom Software Development",
        price: "₦400K-1M",
        description: "Build-to-order applications for businesses",
      },
    },
    "Health & Fitness": {
      starter: {
        name: "30-Day Workout Plans",
        price: "₦5K-10K",
        description: "Home and gym routines with nutrition guides",
      },
      growth: {
        name: "Transformation Coaching Program",
        price: "₦30K-60K",
        description: "12-week program with weekly check-ins",
      },
      premium: {
        name: "VIP Health Transformation",
        price: "₦150K-400K",
        description: "Complete lifestyle overhaul with 1-on-1 support",
      },
    },
    "Personal Development": {
      starter: {
        name: "Goal Setting & Productivity Planner",
        price: "₦6K-12K",
        description: "Digital planners and worksheets",
      },
      growth: {
        name: "Confidence & Success Masterclass",
        price: "₦35K-70K",
        description: "8-module program with live Q&A sessions",
      },
      premium: {
        name: "Life Transformation Coaching",
        price: "₦200K-500K",
        description: "6-month intensive 1-on-1 coaching program",
      },
    },
    "Relationships & Dating": {
      starter: {
        name: "Modern Dating Guide for Nigerians",
        price: "₦5K-10K",
        description: "Practical advice for online and offline dating",
      },
      growth: {
        name: "Relationship Mastery Course",
        price: "₦25K-50K",
        description: "Building and maintaining healthy relationships",
      },
      premium: {
        name: "Couples Counseling Program",
        price: "₦100K-300K",
        description: "Professional relationship coaching sessions",
      },
    },
    "Photography & Video": {
      starter: {
        name: "Mobile Photography Presets",
        price: "₦3K-8K",
        description: "Professional editing presets for smartphones",
      },
      growth: {
        name: "Content Creation Masterclass",
        price: "₦20K-40K",
        description: "From beginner to pro content creator",
      },
      premium: {
        name: "Brand Photography Package",
        price: "₦150K-400K",
        description: "Complete brand shoot + content strategy",
      },
    },
    "Writing & Content Creation": {
      starter: {
        name: "Content Calendar Templates",
        price: "₦4K-8K",
        description: "30 days of content ideas and templates",
      },
      growth: {
        name: "Copywriting Mastery Course",
        price: "₦30K-60K",
        description: "High-converting copy for Nigerian businesses",
      },
      premium: {
        name: "Done-For-You Content Service",
        price: "₦100K-300K",
        description: "Monthly content creation for businesses",
      },
    },
    "Cooking & Food": {
      starter: {
        name: "Nigerian Recipe Collection",
        price: "₦3K-6K",
        description: "50 authentic recipes with video tutorials",
      },
      growth: {
        name: "Food Business Startup Course",
        price: "₦20K-40K",
        description: "From kitchen to profitable food business",
      },
      premium: {
        name: "Restaurant Consulting Package",
        price: "₦200K-500K",
        description: "Complete restaurant setup and menu development",
      },
    },
    "Fashion & Beauty": {
      starter: {
        name: "Style Guide for Nigerian Women",
        price: "₦5K-10K",
        description: "Fashion tips and styling guides",
      },
      growth: {
        name: "Fashion Brand Launch Course",
        price: "₦40K-80K",
        description: "Start and scale your fashion business",
      },
      premium: {
        name: "Personal Styling Service",
        price: "₦100K-250K",
        description: "Complete wardrobe makeover and styling",
      },
    },
    "Real Estate": {
      starter: {
        name: "Property Investment Guide",
        price: "₦8K-15K",
        description: "Beginner's guide to Nigerian real estate",
      },
      growth: {
        name: "Real Estate Wealth Building",
        price: "₦50K-100K",
        description: "Advanced strategies for property investment",
      },
      premium: {
        name: "Property Development Consulting",
        price: "₦500K-2M",
        description: "Full project management and consulting",
      },
    },
    "Other Professional Skill": {
      starter: {
        name: "Skill Monetization Blueprint",
        price: "₦5K-10K",
        description: "Turn any skill into income streams",
      },
      growth: {
        name: "Expert Positioning Course",
        price: "₦30K-60K",
        description: "Become the go-to expert in your field",
      },
      premium: {
        name: "Authority Building Program",
        price: "₦200K-500K",
        description: "Complete personal brand and authority building",
      },
    },
  };

  const products =
    (productsByNiche as any)[knowledge] ||
    productsByNiche["Other Professional Skill"];

  return {
    phase1: {
      ...products.starter,
      targetSales: goalRevenue <= 500000 ? "80-150/month" : "50-100/month",
      timeline: "Month 1-2",
    },
    phase2: {
      ...products.growth,
      targetSales: goalRevenue <= 1000000 ? "15-30/month" : "20-40/month",
      timeline: "Month 3-4",
    },
    phase3: {
      ...products.premium,
      targetSales: goalRevenue <= 3000000 ? "2-8/month" : "5-15/month",
      timeline: "Month 5-6",
    },
  };
};

const getRoadmapPhases = (
  goalRevenue: number,
  currentPotential: number,
  knowledge: KnowledgeArea
): RoadmapPhase[] => {
  const phases: RoadmapPhase[] = [];

  phases.push({
    phase: 1,
    title: "Foundation & First Product",
    timeline: "Months 1-2",
    revenue: Math.round(Math.min(goalRevenue * 0.2, currentPotential * 0.4)),
    keyActions: [
      "Create and launch your starter product",
      "Build email list (target: 500-1000 subscribers)",
      "Set up social media presence",
      "Create 30 days of valuable content",
    ],
    milestone: "First ₦50K-200K month",
  });

  phases.push({
    phase: 2,
    title: "Scale & Premium Offerings",
    timeline: "Months 3-4",
    revenue: Math.round(Math.min(goalRevenue * 0.5, currentPotential * 0.7)),
    keyActions: [
      "Launch mid-tier course or program",
      "Build affiliate partnership network",
      "Create customer success stories",
      "Optimize sales funnel for conversions",
    ],
    milestone: "Consistent ₦300K-600K months",
  });

  phases.push({
    phase: 3,
    title: "Premium & Mastery",
    timeline: "Months 5-6",
    revenue: Math.round(Math.min(goalRevenue, currentPotential)),
    keyActions: [
      "Launch high-ticket premium offering",
      "Build VIP customer community",
      "Create recurring revenue streams",
      "Establish thought leadership",
    ],
    milestone: `Reach your ₦${(goalRevenue / 1000000).toFixed(1)}M goal`,
  });

  return phases;
};

const getStrategy = (potential: number, goal: number): Strategy => {
  const ratio = goal / potential;

  if (ratio <= 1.2) {
    return {
      difficulty: "Highly Achievable 🎯",
      timeline: "3-4 months",
      focus: "Execute the product ladder strategy consistently",
      color: "text-green-600",
      confidence: "95%",
    };
  } else if (ratio <= 2) {
    return {
      difficulty: "Challenging but Realistic 🚀",
      timeline: "6-8 months",
      focus: "Scale audience and add premium offerings",
      color: "text-yellow-600",
      confidence: "80%",
    };
  } else if (ratio <= 3) {
    return {
      difficulty: "Ambitious Growth Required 💪",
      timeline: "10-12 months",
      focus: "Build team, multiple products, and strong systems",
      color: "text-orange-600",
      confidence: "65%",
    };
  } else {
    return {
      difficulty: "Requires Significant Transformation 🔥",
      timeline: "12-18 months",
      focus: "Complete business system overhaul and scaling",
      color: "text-red-600",
      confidence: "45%",
    };
  }
};

export const calculateSimulation = (quizData: QuizData): Simulation => {
  const knowledge =
    (quizData.knowledge as KnowledgeArea) ||
    ("Other Professional Skill" as KnowledgeArea);
  const base = MULTIPLIERS.baseRevenue[knowledge] || 600000;
  const expMult = (MULTIPLIERS.experience[
    quizData.experience as ExperienceLevel as ExperienceLevel
  ] || 1) as number;
  const audMult = (MULTIPLIERS.audience[
    quizData.audience as AudienceSize as AudienceSize
  ] || 1) as number;
  const timeMult = (MULTIPLIERS.time[
    quizData.timeCommitment as TimeCommitment as TimeCommitment
  ] || 1) as number;

  const potentialRevenue = Math.max(
    1000,
    Math.round(base * expMult * audMult * timeMult)
  );
  const goalRevenue = parseGoalRevenue(quizData.goal || "");

  const sim: Simulation = {
    currentPotential: potentialRevenue,
    monthlyGoal: goalRevenue,
    timeToGoal: Math.max(
      1,
      Math.ceil((goalRevenue / Math.max(1, potentialRevenue)) * 6)
    ),
    strategy: getStrategy(potentialRevenue, goalRevenue),
    productLadder: getProductLadder(knowledge, goalRevenue),
    roadmapPhases: getRoadmapPhases(goalRevenue, potentialRevenue, knowledge),
    successProbability: Math.min(
      95,
      Math.max(45, 100 - (goalRevenue / Math.max(1, potentialRevenue) - 1) * 30)
    ),
  };

  return sim;
};
